/**
 * @fileoverview Incident Analysis API Service
 * @description API calls for the incident analysis feature
 * @module features/IncidentAnalysis/services/incidentApi
 */

import { apiClient } from '@/services';

const BASE = '/v1/incidents';

export const incidentApi = {
  /**
   * Health check
   */
  health: () => apiClient.get(`${BASE}/health`),

  /**
   * Load incident CSV data into the backend
   * @param {string} [csvPath] - Optional CSV file path override
   */
  loadData: (csvPath) =>
    apiClient.post(`${BASE}/load`, csvPath ? { csv_path: csvPath } : {}),

  /**
   * Fetch incident records.
   * @param {Object} params
   * @param {number} [params.after_id] - Fetch records > this ID
   * @param {number} [params.limit=50] - Max records to return
   */
  fetchRecords: (params = {}) => apiClient.get(`${BASE}/records`, params),

  /**
   * Fetch the latest N records
   * @param {number} [limit=50]
   */
  fetchLatest: (limit = 50) =>
    apiClient.get(`${BASE}/records/latest`, { limit }),

  /**
   * Fetch a single incident by ID
   * @param {number} incidentId
   */
  fetchById: (incidentId) => apiClient.get(`${BASE}/records/${incidentId}`),

  /**
   * Check HuggingFace collection status
   */
  collectionStatus: () => apiClient.get(`${BASE}/collection/status`),

  /**
   * Build or rebuild the HuggingFace collection
   * @param {boolean} [forceRebuild=false]
   */
  buildCollection: (forceRebuild = false) =>
    apiClient.post(`${BASE}/collection/build`, {
      force_rebuild: forceRebuild,
    }),

  /**
   * Run full analysis (semantic + agentic) on an incident
   * @param {number} incidentId
   */
  analyzeIncident: (incidentId) =>
    apiClient.post(`${BASE}/analyze`, { incident_id: incidentId }),

  /**
   * Run semantic-only analysis on an incident
   * @param {number} incidentId
   */
  analyzeSemanticOnly: (incidentId) =>
    apiClient.post(`${BASE}/analyze/semantic`, { incident_id: incidentId }),

  /**
   * Load CSV + Build collection + Analyze all incidents in one pipeline.
   * Returns immediately with 202; poll batchStatus() for progress.
   * @param {string} [csvPath] - Optional CSV file path override
   */
  loadAndAnalyzeAll: (csvPath) =>
    apiClient.post(`${BASE}/load-and-analyze`, csvPath ? { csv_path: csvPath } : {}),

  /**
   * Get batch analysis pipeline progress
   */
  batchStatus: () => apiClient.get(`${BASE}/analyze/batch/status`),

  /**
   * Get all batch analysis results
   */
  batchResults: () => apiClient.get(`${BASE}/analyze/batch/results`),

  // ── Scoped Report Generation ────────────────────────────────

  /**
   * Start a scoped batch report for a date range or incident limit.
   * Returns 202 immediately; poll reportStatus() for progress.
   * @param {{ start_date?: string, end_date?: string, limit?: number, incident_ids?: number[] }} params
   */
  generateReport: (params = {}) => apiClient.post(`${BASE}/report`, params),

  /**
   * Poll the progress of an in-progress report generation.
   */
  reportStatus: () => apiClient.get(`${BASE}/report/status`),

  /**
   * Retrieve the report rows accumulated so far (safe to poll while running).
   * @param {{ run_id?: number }} params
   */
  reportResults: (params = {}) => apiClient.get(`${BASE}/report/results`, params),

  /**
   * Retrieve recent report runs for the current user.
   * @param {{ limit?: number }} params
   */
  reportHistory: (params = {}) => apiClient.get(`${BASE}/report/history`, params),

  /**
   * Request cancellation for the current report generation.
   */
  cancelReport: () => apiClient.post(`${BASE}/report/cancel`, {}),
};

export default incidentApi;
